import React from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContainer = styled.div`
  background: white;
  padding: 25px;
  width: 90%;
  max-width: 800px;
  border-radius: 10px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;

  @media (max-width: 768px) {
    padding: 15px;
    width: 95%;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  color: #1c4e8c;
  font-size: 22px;
`;

const CloseButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 6px 12px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #c0392b;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #1c4e8c, #4a90e2);
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #367bc3;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 16px;

  th, td {
    border: 1px solid #ccc;
    padding: 8px;
    text-align: right;
  }

  th {
    background-color: #1c4e8c;
    color: white;
    font-weight: bold;
  }

  tr:hover {
    background-color: #f9f9f9;
  }

  @media (max-width: 768px) {
    font-size: 12px;
    th, td {
      padding: 6px;
    }
  }
`;

const MetadataBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
  background: #f9fbfd;
  border: 1px solid #d6e0ef;
  padding: 15px;
  border-radius: 8px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    font-size: 14px;
  }
`;

const MetaItem = styled.div`
  strong {
    color: #1c4e8c;
  }
`;

const DocDetailsModal = ({ docCode, items, onClose }) => {
    const exportToExcel = () => {
  if (!items.length) return;

  const exportData = items.map(item => ({
    'מספר מסמך': item.Doc_Code,
    'לקוח': item.Cust_Name,
    'ספק': item.Sup_Name,
    'שם אוניה': item.Store_Name,
    'תאריך': new Date(parseInt(item.Date.match(/\d+/)[0])).toLocaleString('he-IL', {
      dateStyle: 'short',
      timeStyle: 'short',
      hour12: false
    }),
    'נהג': item.DriverName,
    'מספר רכב': item.TruckNum,
    'יעד': item.Additional_1,
    'מק"ט': item.Makat,
    'תיאור': item.Des,
    'כמות': item.Qty,
    'משקל': item.Weight
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Doc_${docCode}`);
  XLSX.writeFile(wb, `doc_${docCode}.xlsx`);
};


  const printDoc = () => {
  if (!items.length) return;

  const doc = items[0];
  const metadata = `
    <div style="margin-bottom: 20px; font-size: 16px;">
      <div><strong>תאריך:</strong> ${new Date(parseInt(doc.Date.match(/\d+/)[0])).toLocaleString('he-IL')}</div>
      <div><strong>לקוח:</strong> ${doc.Cust_Name}</div>
      <div><strong>ספק:</strong> ${doc.Sup_Name}</div>
      <div><strong>אוניה:</strong> ${doc.Store_Name}</div>
      <div><strong>נהג:</strong> ${doc.DriverName}</div>
      <div><strong>מספר רכב:</strong> ${doc.TruckNum}</div>
      <div><strong>יעד:</strong> ${doc.Additional_1}</div>
      <div><strong>סה"כ כמות:</strong> ${items.reduce((sum, i) => sum + (i.Qty || 0), 0)}</div>
      <div><strong>סה"כ משקל:</strong> ${doc.Weight}</div>
    </div>
  `;

  const tableHTML = document.getElementById('modalTableContent').outerHTML;

  const newWindow = window.open('', '_blank');
  newWindow.document.write(`
    <html>
      <head>
        <title>הדפסת מסמך ${docCode}</title>
        <style>
          body { font-family: Arial; direction: rtl; padding: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid black; padding: 10px; text-align: right; }
          th { background-color: #1c4e8c; color: white; }
        </style>
      </head>
      <body>
        <h2>מסמך מספר ${docCode}</h2>
        ${metadata}
        ${tableHTML}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
    </html>
  `);
  newWindow.document.close();
};


  return (
    <Overlay>
      <ModalContainer>
        <ModalHeader>
          <Title>פרטי מסמך: {docCode}</Title>
          <CloseButton onClick={onClose}>סגור</CloseButton>
        </ModalHeader>

        <ActionRow>
          <ActionButton onClick={exportToExcel}>📥 יצוא לאקסל</ActionButton>
          <ActionButton onClick={printDoc}>🖨️ הדפס</ActionButton>
        </ActionRow>

        <MetadataBlock>
        <MetaItem><strong>תאריך:</strong> {new Date(parseInt(items[0].Date.match(/\d+/)[0])).toLocaleString('he-IL')}</MetaItem>
        <MetaItem><strong>לקוח:</strong> {items[0].Cust_Name}</MetaItem>
        <MetaItem><strong>ספק:</strong> {items[0].Sup_Name}</MetaItem>
        <MetaItem><strong>אוניה:</strong> {items[0].Store_Name}</MetaItem>
        <MetaItem><strong>נהג:</strong> {items[0].DriverName}</MetaItem>
        <MetaItem><strong>מספר רכב:</strong> {items[0].TruckNum}</MetaItem>
        <MetaItem><strong>יעד:</strong> {items[0].Additional_1}</MetaItem>
        <MetaItem><strong>סה"כ כמות:</strong> {items.reduce((sum, i) => sum + (i.Qty || 0), 0)}</MetaItem>
        <MetaItem><strong>סה"כ משקל:</strong> {items[0].Weight}</MetaItem>
        </MetadataBlock>


        <div id="modalTableContent">
          <Table>
            <thead>
                <tr>
                    <th>מק״ט</th>
                    <th>תיאור</th>
                    <th>כמות</th>
                </tr>
                </thead>
                <tbody>
                {items.map((item, idx) => (
                    <tr key={idx}>
                    <td>{item.Makat}</td>
                    <td>{item.Des}</td>
                    <td>{item.Qty}</td>
                    </tr>
                ))}
                </tbody>
          </Table>
        </div>
      </ModalContainer>
    </Overlay>
  );
};

export default DocDetailsModal;
